'use strict';
import React, { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
	Box,
	IconButton,
	CircularProgress
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { useLearningCenterLinksQuery } from '@/redux/services/portal/user.api.js';
import { normalizeLearningCenterLinks } from '@/utils/learningCenterLinks';

const LinkRow = ({ label, url, onClose }) => (
	<ListItem disablePadding>
		<ListItemButton
			component="a"
			href={url}
			target="_blank"
			rel="noopener noreferrer"
			onClick={() => onClose()}
			sx={{
				borderRadius: 2,
				mb: 1,
				'&:hover': {
					background: Brand.metaboliteWhite
				}
			}}
		>
			<ListItemText
				primary={
					<Typography variant="subtitle1" sx={{ fontWeight: 600, color: Brand.darkModeBlue }}>
						{label}
					</Typography>
				}
			/>
			<OpenInNew sx={{ color: Brand.molecularBlue, ml: 1 }} fontSize="small" />
		</ListItemButton>
	</ListItem>
);

export const LearningCenterLinksModal = ({ open, onClose }) => {
	const { isAuthenticated } = useAuth0();
	const { data, isLoading, isFetching, error } = useLearningCenterLinksQuery(undefined, {
		skip: !open || !isAuthenticated,
	});
	const links = useMemo(() => normalizeLearningCenterLinks(data), [data]);
	const isLoadingLinks = isLoading || isFetching;
	const hasLinks = links.length > 0;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					border: `1px solid ${Brand.precisionGrey}`
				}
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
				<Typography variant="h6" component="span" sx={{ fontWeight: 700, color: Brand.darkModeBlue }}>
					Learning Center
				</Typography>
				<IconButton onClick={onClose} aria-label="close" size="small">
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{isLoadingLinks ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress sx={{ color: Brand.insightfulOrange }} />
					</Box>
				) : error ? (
					<Box sx={{ py: 2 }}>
						<Typography variant="body2" sx={{ color: Brand.activatedRed }}>
							Unable to load Learning Center links. Please try again or contact your Metabolon representative.
						</Typography>
					</Box>
				) : hasLinks ? (
					<List sx={{ pt: 0 }}>
						{links.map((item) => (
							<LinkRow key={item.branchId || item.url} label={item.label} url={item.url} onClose={onClose} />
						))}
					</List>
				) : (
					<Box sx={{ py: 2 }}>
						<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
							You have Learning Center access, but launch URLs are not configured yet. Contact your Metabolon
							representative if this persists.
						</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default LearningCenterLinksModal;
